/**
 * Copy-paste prompt that lets an AI coding assistant (ChatGPT, Claude,
 * Cursor…) integrate a saved style: the style URL, how to handle the API key,
 * where to find the current SDK version and the integration gotchas.
 */
import { GL_JS_NPM_URL, GL_JS_PACKAGE } from "./embed-snippets";

/** Stands in for the key when the user doesn't want it in the prompt. */
export const API_KEY_PLACEHOLDER = "YOUR_VIETMAP_API_KEY";

const CONSOLE_URL = "https://maps.vietmap.vn/console";
const DOCS_URL = "https://maps.vietmap.vn/docs/sdk-web-gl/";

export type AiPromptInput = {
  name: string;
  /** Display label of the base theme, e.g. "Đường phố". */
  theme?: string;
  description?: string;
  /** Style URL with the real key, or with API_KEY_PLACEHOLDER. */
  styleUrl: string;
  /** false → the URL carries API_KEY_PLACEHOLDER and the AI must ask for it. */
  includesKey: boolean;
  /** Newest stable SDK version known when the prompt was generated. */
  glJsVersion: string;
  lang: "vi" | "en";
};

/** Replace the `apikey` query value of a style URL with the placeholder. */
export function maskApiKey(styleUrl: string): string {
  return styleUrl.replace(/([?&]api[kK]ey=)[^&#]*/, `$1${API_KEY_PLACEHOLDER}`);
}

export function buildAiPrompt(p: AiPromptInput): string {
  return p.lang === "en" ? promptEn(p) : promptVi(p);
}

function styleInfo(p: AiPromptInput, labels: { name: string; theme: string; desc: string }): string {
  return [
    `- ${labels.name}: ${p.name}`,
    p.theme ? `- ${labels.theme}: ${p.theme}` : "",
    p.description ? `- ${labels.desc}: ${p.description}` : "",
  ].filter(Boolean).join("\n");
}

function promptVi(p: AiPromptInput): string {
  const key = p.includesKey
    ? `URL trên đã kèm API key của tôi (tham số \`apikey\`). Khi viết code:
- Không hard-code key trong source. Tách key ra biến môi trường theo framework của dự án (vd. \`VITE_VIETMAP_API_KEY\`, \`NEXT_PUBLIC_VIETMAP_API_KEY\`, \`.env\` / \`--dart-define\` cho mobile) rồi ghép vào URL lúc chạy.
- Nếu tôi muốn dùng key khác, chỉ cần thay giá trị tham số \`apikey\` — không phải sửa gì khác.`
    : `URL trên dùng placeholder \`${API_KEY_PLACEHOLDER}\` thay cho API key thật.
- TRƯỚC KHI viết code, hãy hỏi tôi API key (lấy tại ${CONSOLE_URL}). Nếu tôi chưa có, dùng biến môi trường (vd. \`VITE_VIETMAP_API_KEY\`) và nhắc tôi điền sau.
- Key được truyền qua tham số \`apikey\` của URL — chỉ cần thay placeholder, không sửa gì khác.`;

  return `Bạn là lập trình viên, hãy giúp tôi tích hợp bản đồ Vietmap với style tuỳ biến dưới đây vào dự án của tôi.

## Style bản đồ
${styleInfo(p, { name: "Tên", theme: "Style gốc", desc: "Mô tả" })}
- Style URL (chuẩn MapLibre/Mapbox Style Spec, trả về style.json):
  ${p.styleUrl}

URL này trả về style.json đầy đủ; mọi URL tile / sprite / glyph bên trong đã được gắn sẵn API key từ tham số \`apikey\`. Truyền thẳng URL vào tham số \`style\` của SDK — không cần tải về hay sửa style.json.

## API key
${key}
- Với SDK web/mobile, key nằm ở phía client là bình thường. Hãy nhắc tôi giới hạn domain / app cho key trong Vietmap Console.

## Thư viện (Web)
- Package: \`${GL_JS_PACKAGE}\` — ${GL_JS_NPM_URL}
- Kiểm tra trang npm trên (hoặc chạy \`npm view ${GL_JS_PACKAGE} versions\`) để lấy phiên bản ỔN ĐỊNH mới nhất. Lưu ý dist-tag \`latest\` có thể đang trỏ tới bản pre-release (vd. \`7.0.0-pre.1\`) — không dùng bản \`-pre\` / \`-alpha\` trừ khi tôi yêu cầu. Lúc tạo prompt này, bản ổn định mới nhất là \`${p.glJsVersion}\`.
- API gần như giống hệt MapLibre GL JS (fork của MapLibre): \`new vietmapgl.Map(...)\`, cùng method, cùng style spec.

## Cách tích hợp
1. Cài: \`npm i ${GL_JS_PACKAGE}@<phiên bản ổn định mới nhất>\`.
2. Import — package KHÔNG có field \`main\`/\`exports\`, nên phải import từ \`dist/\`:
   \`import vietmapgl from "${GL_JS_PACKAGE}/dist/vietmap-gl";\`
   \`import "${GL_JS_PACKAGE}/dist/vietmap-gl.css";\` (bắt buộc — thiếu CSS thì control, popup, marker bị vỡ)
   Dùng CDN thì: \`https://unpkg.com/${GL_JS_PACKAGE}@<version>/dist/vietmap-gl.js\` + \`vietmap-gl.css\`, biến global là \`vietmapgl\`.
3. Container của map phải có chiều cao cụ thể (vd. \`height: 480px\` hoặc \`100vh\`), nếu không map sẽ cao 0px.
4. Khởi tạo: \`new vietmapgl.Map({ container, style: STYLE_URL, center: [lng, lat], zoom })\`. Toạ độ trong GL JS là \`[kinh độ, vĩ độ]\` (GeoJSON) — ngược với nhiều REST API của Vietmap dùng \`lat,lng\`.
5. Chỉ \`addSource\` / \`addLayer\` / marker sau \`map.on("load", …)\`.
6. React / Vue / Angular: tạo map trong \`useEffect\` / \`onMounted\` / \`ngAfterViewInit\`, gọi \`map.remove()\` khi unmount, và chặn khởi tạo 2 lần (React StrictMode).
7. Mobile dùng cùng Style URL: Flutter \`vietmap_flutter_gl\` (\`styleString\`), React Native \`@vietmap/vietmap-gl-react-native\` (\`mapStyle\`), Android \`com.github.vietmap-company:maps-sdk-android\`, iOS qua CocoaPods.
8. Tài liệu: ${DOCS_URL}

## Ví dụ tối thiểu (HTML)
\`\`\`html
<link href="https://unpkg.com/${GL_JS_PACKAGE}@${p.glJsVersion}/dist/vietmap-gl.css" rel="stylesheet" />
<script src="https://unpkg.com/${GL_JS_PACKAGE}@${p.glJsVersion}/dist/vietmap-gl.js"></script>
<div id="map" style="width:100%;height:480px"></div>
<script>
  const map = new vietmapgl.Map({
    container: "map",
    style: "${p.styleUrl}",
    center: [108, 14.5],
    zoom: 5,
  });
  map.addControl(new vietmapgl.NavigationControl());
</script>
\`\`\`

## Việc cần làm
1. Nếu chưa rõ, hỏi tôi: framework / nền tảng đang dùng, vị trí trung tâm và mức zoom ban đầu${p.includesKey ? "" : ", và API key"}.
2. Viết code tích hợp hoàn chỉnh cho dự án của tôi theo các lưu ý ở trên, kèm lệnh cài đặt và chỗ đặt biến môi trường.
3. Trả lời bằng tiếng Việt.`;
}

function promptEn(p: AiPromptInput): string {
  const key = p.includesKey
    ? `The URL above already carries my API key (the \`apikey\` query parameter). When writing code:
- Do not hard-code the key. Move it to an environment variable that fits the project (e.g. \`VITE_VIETMAP_API_KEY\`, \`NEXT_PUBLIC_VIETMAP_API_KEY\`, \`.env\` / \`--dart-define\` on mobile) and build the URL at runtime.
- To use a different key, only replace the \`apikey\` parameter value — nothing else changes.`
    : `The URL above uses the placeholder \`${API_KEY_PLACEHOLDER}\` instead of a real API key.
- BEFORE writing code, ask me for my API key (get one at ${CONSOLE_URL}). If I don't have it yet, read it from an environment variable (e.g. \`VITE_VIETMAP_API_KEY\`) and remind me to fill it in.
- The key travels in the URL's \`apikey\` parameter — just replace the placeholder.`;

  return `You are a developer. Help me integrate a Vietmap map with the custom style below into my project.

## Map style
${styleInfo(p, { name: "Name", theme: "Base style", desc: "Description" })}
- Style URL (MapLibre/Mapbox Style Spec, returns style.json):
  ${p.styleUrl}

The URL returns the full style.json; every tile / sprite / glyph URL inside already carries the API key from the \`apikey\` parameter. Pass the URL straight to the SDK's \`style\` option — no need to download or edit style.json.

## API key
${key}
- With web/mobile SDKs the key is client-side by design. Remind me to restrict it by domain / app in the Vietmap Console.

## Library (Web)
- Package: \`${GL_JS_PACKAGE}\` — ${GL_JS_NPM_URL}
- Check that npm page (or run \`npm view ${GL_JS_PACKAGE} versions\`) for the newest STABLE version. Note the \`latest\` dist-tag may point at a pre-release (e.g. \`7.0.0-pre.1\`) — don't use \`-pre\` / \`-alpha\` builds unless I ask. When this prompt was generated the newest stable version was \`${p.glJsVersion}\`.
- The API mirrors MapLibre GL JS (it is a MapLibre fork): \`new vietmapgl.Map(...)\`, same methods, same style spec.

## How to integrate
1. Install: \`npm i ${GL_JS_PACKAGE}@<newest stable version>\`.
2. Import — the package has NO \`main\`/\`exports\` field, so import from \`dist/\`:
   \`import vietmapgl from "${GL_JS_PACKAGE}/dist/vietmap-gl";\`
   \`import "${GL_JS_PACKAGE}/dist/vietmap-gl.css";\` (required — without it controls, popups and markers break)
   Via CDN: \`https://unpkg.com/${GL_JS_PACKAGE}@<version>/dist/vietmap-gl.js\` + \`vietmap-gl.css\`, global \`vietmapgl\`.
3. Give the map container an explicit height (e.g. \`height: 480px\` or \`100vh\`), otherwise it renders 0px tall.
4. Create: \`new vietmapgl.Map({ container, style: STYLE_URL, center: [lng, lat], zoom })\`. GL JS coordinates are \`[lng, lat]\` (GeoJSON) — the opposite of many Vietmap REST APIs, which use \`lat,lng\`.
5. Only \`addSource\` / \`addLayer\` / add markers after \`map.on("load", …)\`.
6. React / Vue / Angular: create the map in \`useEffect\` / \`onMounted\` / \`ngAfterViewInit\`, call \`map.remove()\` on unmount, and guard against double init (React StrictMode).
7. Mobile uses the same Style URL: Flutter \`vietmap_flutter_gl\` (\`styleString\`), React Native \`@vietmap/vietmap-gl-react-native\` (\`mapStyle\`), Android \`com.github.vietmap-company:maps-sdk-android\`, iOS via CocoaPods.
8. Docs: ${DOCS_URL}

## Minimal example (HTML)
\`\`\`html
<link href="https://unpkg.com/${GL_JS_PACKAGE}@${p.glJsVersion}/dist/vietmap-gl.css" rel="stylesheet" />
<script src="https://unpkg.com/${GL_JS_PACKAGE}@${p.glJsVersion}/dist/vietmap-gl.js"></script>
<div id="map" style="width:100%;height:480px"></div>
<script>
  const map = new vietmapgl.Map({
    container: "map",
    style: "${p.styleUrl}",
    center: [108, 14.5],
    zoom: 5,
  });
  map.addControl(new vietmapgl.NavigationControl());
</script>
\`\`\`

## What to do
1. If unclear, ask me: which framework / platform I use, the initial center and zoom${p.includesKey ? "" : ", and my API key"}.
2. Write complete integration code for my project following the notes above, including install commands and where the environment variable goes.`;
}
